import React from 'react';
import { shallowEqual } from 'react-redux';
import { LayoutOrientations } from '../../globals';
import store, { StoreActions } from '../../state/store';
import { ClearIcon } from '../Iconset';
import Panel from '../layout/Panel';
import Spacer from '../layout/Spacer';
import { TooltipKeys } from "../tooltips/TooltipKeys";
import { useTypedSelector } from '../../state/hooks';
import useInterval from '../reactUtils/UseInterval';
import { getUuidv4 } from '../../classes/utilities/Misc';

export interface INotificationInfo
{
	content: JSX.Element | string;
	indismissable?: boolean;
	timeout?: number;
}

const notificationsMap = new Map();

/**
 * Add a notification to the screen
 *
 * pass an object containing the required information to properly display the notification to the screen, with the following format:
 * - content: this is the actual visible part of the notification. it can be any kind of jsx code
 * - indismissable: if set to 'true', the notification won't display the dismiss button *IMPORTANT: timeout should be set in this case or the notification will never go away*
 * - timeout: time in seconds until the notification is automatically dismissed
 *
 * @param {*} notificationInfo
 * @returns the guid of the notification instance
 */
NotificationPrompter.prompt = function (notificationInfo)
{
	const notificationId = getUuidv4();
	notificationsMap.set(notificationId, notificationInfo);
	store.dispatch(StoreActions.workspace.addNotification(notificationId));
	return notificationId;
}

/**
 * Removes a notification from the screen
 *
 * @param {*} notificationId the guid of the nitification to remove
 */
NotificationPrompter.removeNotification = function (notificationId)
{
	notificationsMap.delete(notificationId);
	store.dispatch(StoreActions.workspace.removeNotification(notificationId));
}

/**
 * Removes *all* the notifications from the screen
 *
 */
NotificationPrompter.clearNotifications = function ()
{
	notificationsMap.clear();
	store.dispatch(StoreActions.workspace.clearNotifications());
}

export default function NotificationPrompter(props)
{
	const notifications = useTypedSelector(state => state.workspaceModule.notifications, shallowEqual);

	return (
		<div className={"notificationPrompterRoot"}
			data-name={"notifications-prompter-root"}
		>
			{
				Object.entries(notifications).map(([key, value], idx) =>
				{
					if (!notificationsMap.has(key)) return null;

					return (
						<Notification key={idx}
							id={key}
							info={notificationsMap.get(key)}
						/>
					);
				})
			}
		</div>
	);
}

function Notification(props)
{
	const timeout = props.info.timeout ? props.info.timeout * 1000 : 0;
	
	if (timeout)
	{
		useInterval(() => 
		{
			NotificationPrompter.removeNotification(props.id);
		}, timeout);	
	}

	return (
		<Panel className={"notificationRoot dark composite"}
			data-name={"autosave-notification-root"}
			orientation={LayoutOrientations.Horizontal}
		>
			<Panel className={"notificationContent"}
				orientation={LayoutOrientations.Vertical}
			>
				{props.info.content}
			</Panel>
			<Spacer size={{ x: "4px" }} />
			<Panel className={"notificationButtons"}
				orientation={LayoutOrientations.Vertical}
			>
				{
					props.info.indismissable ? null :
						<ClearIcon data-name={"notification-dismiss"}
							onClick={() => NotificationPrompter.removeNotification(props.id)}
							tooltip={TooltipKeys.NotificationDismiss}
						/>
				}
			</Panel>
		</Panel>
	);
}